/**
 * ローディング / 空 / エラーの三態を、画面ごとに書き分けないための小物（05-ui §11.2）。
 */
import * as React from 'react';
import { Loader2, RefreshCw, ShieldOff } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle, Button, EmptyState, Skeleton } from '@mail-sv/ui';
import { ApiError } from '../lib/api.js';
import { PERMISSION_DENIED_MESSAGE } from '../lib/permissions.js';

/** エラーを日本語 1 文にする。403 だけは API の文言より具体的な案内に差し替える。 */
export function errorMessageOf(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.isForbidden) return PERMISSION_DENIED_MESSAGE;
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return '不明なエラーが発生しました。';
}

export function QueryError({
  error,
  onRetry,
  className,
}: {
  error: unknown;
  onRetry?: () => void;
  className?: string;
}): React.ReactElement {
  const forbidden = error instanceof ApiError && error.isForbidden;
  return (
    <Alert variant="destructive" className={className}>
      <AlertTitle>{forbidden ? '権限がありません' : '読み込みに失敗しました'}</AlertTitle>
      <AlertDescription className="space-y-3">
        <p>{errorMessageOf(error)}</p>
        {/* 403 に「再試行」を出すと、押せば通ると誤解させる。 */}
        {!forbidden && onRetry ? (
          <Button variant="outline" size="sm" onClick={onRetry}>
            <RefreshCw aria-hidden />
            再試行
          </Button>
        ) : null}
      </AlertDescription>
    </Alert>
  );
}

/** 再フェッチ中の控えめなインジケータ。PageHeader の actions に置く。 */
export function RefetchingDot({ active }: { active: boolean }): React.ReactElement | null {
  if (!active) return null;
  return (
    <Loader2
      className="size-3.5 animate-spin text-muted-foreground"
      aria-label="最新の情報を取得しています"
    />
  );
}

/** 権限が無いタブ・パネルの中身。 */
export function PermissionDenied({ className }: { className?: string }): React.ReactElement {
  return (
    <EmptyState
      icon={ShieldOff}
      title="権限がありません"
      description={PERMISSION_DENIED_MESSAGE}
      className={className}
    />
  );
}

/** StatCard 行のスケルトン。スピナー単体は使わない（レイアウトシフトを避ける）。 */
export function StatCardSkeleton({ count = 4 }: { count?: number }): React.ReactElement {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-xl border border-border bg-card p-4">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="mt-3 h-7 w-20" />
          <Skeleton className="mt-2 h-3 w-32" />
        </div>
      ))}
    </div>
  );
}

/** 詳細画面のスケルトン。 */
export function DetailSkeleton(): React.ReactElement {
  return (
    <div className="space-y-4">
      <Skeleton className="h-6 w-48" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="space-y-2">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-4 w-40" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** 表示するデータが無いときの本文。フィルタの有無で文言を変える。 */
export function ListEmpty({
  filtered,
  entityLabel,
  onClearFilter,
  action,
}: {
  filtered: boolean;
  entityLabel: string;
  onClearFilter?: () => void;
  action?: React.ReactNode;
}): React.ReactElement {
  if (filtered) {
    return (
      <EmptyState
        title="条件に一致する項目がありません"
        description="フィルタを外すと全件を表示します。"
        action={
          onClearFilter ? (
            <Button variant="outline" size="sm" onClick={onClearFilter}>
              フィルタをクリア
            </Button>
          ) : undefined
        }
      />
    );
  }
  return <EmptyState title={`${entityLabel}はまだありません`} action={action} />;
}
